const BPS = 10_000n;
const E18 = 10n ** 18n;

/**
 * The most SYN one bond can buy right now and what that costs in the market's asset. The contract allows
 * `payout ≤ min(maxPayout, capacity left)`, with payout = value × (1 + ROI) / price = value / bond price, so the
 * largest payment is limit × bond price / (USD value of one asset unit). A `marginBps` haircut keeps a payment typed
 * at the limit inside it while the average price and ROI drift between quote and transaction.
 */
export function bondLimit(
  maxPayout: bigint | undefined,
  available: bigint | undefined,
  bondPriceUsd: bigint | undefined, // 18 dec, USD per SYN at the bond's discount
  unitValueUsd: bigint | undefined, // 18 dec, USD value of 1e18 of the asset
  marginBps = 50n,
): { payout: bigint; amount: bigint; byCapacity: boolean } | null {
  if (maxPayout === undefined || available === undefined || !bondPriceUsd || !unitValueUsd) return null;
  const byCapacity = available < maxPayout;
  const payout = byCapacity ? available : maxPayout;
  const amount = (((payout * bondPriceUsd) / unitValueUsd) * (BPS - marginBps)) / BPS;
  return { payout, amount, byCapacity };
}

/** How far the pool's spot price is from its average, in bps, and whether bonds are paused by it (the contract's rule). */
export function priceGuard(spot: bigint | undefined, twap: bigint | undefined, maxBps: number | undefined) {
  if (spot === undefined || !twap || maxBps === undefined) return null;
  const diff = spot > twap ? spot - twap : twap - spot;
  return { devBps: Number((diff * BPS) / twap), maxBps, paused: diff * BPS > twap * BigInt(maxBps) };
}

/**
 * When the oracle's average next moves: the newest checkpoint turns a full period old and becomes the base of the
 * average, which then covers only what the pool did since. Bonds paused by a price swing can reopen then.
 */
export function reopensAt(newestCheckpoint: number, twapPeriod: number): number {
  return newestCheckpoint + twapPeriod;
}

export { E18 };
